import { useMemo, useRef } from "react";
import {
  $getRoot,
  HISTORY_MERGE_TAG,
  HISTORY_PUSH_TAG,
  LexicalEditor,
  UNDO_COMMAND,
} from "lexical";

import { PostFieldLabel, PlatformUsage } from "components/Posts/PostForm";
import { countCharacter } from "utils/strings";
import { useRegisterFlush } from "../platform-fields/flush-registry";
import PostFieldError from "components/Posts/PostForm/PostFieldError/PostFieldError";
import { FIELDS } from "constants/PostFormFields";
import { useDebouncedCallback } from "use-debounce";
import { useSkedCaptionAutocomplete } from "ui/SkedCaption";
import { SkedCaptionV2Lazy as SkedCaptionV2 } from "@/ui/SkedCaption/v2/sked-caption-v2.lazy";
import { skedCaptionImport } from "@/ui/SkedCaption/v2/sked-caption-v2";
import {
  DEFAULT_HASHTAG_LIMIT,
  getStrictestRuleMax,
  RULES,
} from "../postPlatforms.config";
import { Post } from "../../../../features/post";
import { PlatformSpec } from "../../../../constants/Platforms";
import {
  FormValidationErrors,
  FormValidationWarnings,
} from "../../../../shared/types/post";
import {
  AccountPlatformType,
  PostLinkedInAccountType,
} from "../../../../shared/types/accounts";
import type { Label } from "../../../../entities/posts/components/templates/post-form-base-template";
import { InlineAiCaptionPopover } from "../PostCaption/inline-ai-caption-popover";

const PostFirstComment = ({
  post,
  selectedPlatform,
  platforms,
  onChange,
  className = "",
  errors,
  warnings,
  disabled = false,
  hashtagCount,
  hasMoreThanOnelinkedInAccount,
  selectedLinkedInAccountType,
  selectedLinkedUrn,
  formId,
  selectedContentLabel,
  accountGroupId,
  labelIds,
}: {
  post: Post;
  selectedPlatform: AccountPlatformType | "LINKED";
  platforms: PlatformSpec[];
  onChange: (value: string) => void;
  className?: string;
  errors?: FormValidationErrors;
  warnings?: FormValidationWarnings;
  disabled?: boolean;
  hashtagCount: number;
  hasMoreThanOnelinkedInAccount?: boolean;
  selectedLinkedInAccountType?: PostLinkedInAccountType;
  selectedLinkedUrn?: string;
  formId?: string;
  selectedContentLabel?: Label[];
  accountGroupId?: string;
  labelIds?: string[];
}) => {
  const editorRef = useRef<LexicalEditor | null>(null);
  const skedCaptionAutocomplete = useSkedCaptionAutocomplete(selectedPlatform);
  const firstcomment = useMemo(() => post.firstcomment || "", [post]);
  const suggestionPlatforms = useMemo(
    () => [
      ...new Set(
        (platforms.length > 0
          ? platforms.map(({ TYPE }) => TYPE)
          : [selectedPlatform]
        ).map((platform) => (platform === "LINKED" ? "LI" : platform))
      ),
    ],
    [platforms, selectedPlatform]
  );
  const mentionCount = countCharacter(firstcomment, "@");
  const maxHashtagCount = useMemo(() => {
    return getStrictestRuleMax({
      platforms,
      selectedPlatform,
      field: FIELDS.FIRST_COMMENT,
      rule: RULES.HASHTAG_COUNT,
    });
  }, [platforms, selectedPlatform]);

  const bounce = useDebouncedCallback((value) => {
    if (value !== firstcomment) {
      onChange(value);
    }
  }, 500);
  useRegisterFlush(bounce.flush);

  return (
    <div data-testid="postFirstComment" className={className}>
      <div className="flex items-center">
        <PostFieldLabel className="flex items-center mb-2">
          <div className="mr-2"> First Comment</div>
          <PlatformUsage
            platforms={platforms}
            field={FIELDS.FIRST_COMMENT}
            selectedPlatform={selectedPlatform}
          />
        </PostFieldLabel>
      </div>
      <SkedCaptionV2
        className="min-h-[116px]"
        selectedPlatform={selectedPlatform}
        initialValue={firstcomment}
        disabled={disabled}
        onChange={bounce}
        suggestions={true}
        descriptionsHidden={true}
        autocomplete={skedCaptionAutocomplete}
        hasMoreThanOnelinkedInAccount={hasMoreThanOnelinkedInAccount}
        selectedLinkedInAccountType={selectedLinkedInAccountType}
        selectedLinkedUrn={selectedLinkedUrn}
        onEditorContextReady={({ editor }) => {
          editorRef.current = editor;
        }}
        extraActions={
          <InlineAiCaptionPopover
            disabled={disabled}
            currentCaption={firstcomment}
            source="first_comment"
            groupId={accountGroupId}
            labelIds={labelIds}
            selectedContentLabels={selectedContentLabel}
            platforms={suggestionPlatforms}
            formId={formId}
            onUndo={() => {
              // Lexical commands with a void payload still require the argument.
              // eslint-disable-next-line unicorn/no-useless-undefined
              editorRef.current?.dispatchCommand(UNDO_COMMAND, undefined);
            }}
            onApply={(nextComment, options) => {
              if (!editorRef.current) {
                onChange(nextComment);
                return;
              }

              editorRef.current.update(
                () => {
                  const root = $getRoot();
                  root.clear();
                  root.append(skedCaptionImport(nextComment));
                },
                options
                  ? {
                      tag:
                        options.history === "push"
                          ? HISTORY_PUSH_TAG
                          : HISTORY_MERGE_TAG,
                    }
                  : undefined
              );
            }}
          />
        }
        counters={{
          characters: firstcomment.length,
          charactersMax: 2200,
          hashtags: hashtagCount,
          hashtagsMax: maxHashtagCount ?? DEFAULT_HASHTAG_LIMIT,
          mentions: mentionCount > 0 ? mentionCount : undefined,
        }}
      />

      <PostFieldError
        className="mt-4"
        fields={[FIELDS.FIRST_COMMENT]}
        errors={errors}
        warnings={warnings}
      />
    </div>
  );
};

export default PostFirstComment;
